// src/components/InventoryHistoryModal.js
import React, { useEffect, useState } from 'react';
import { FiActivity, FiTruck, FiUser } from 'react-icons/fi';
import Modal from './Modal';
import { useApi } from '../hooks/useApi';
import { useIsMobile } from '../hooks/useIsMobile'; // ✅ Import Hook

const InventoryHistoryModal = ({ isOpen, onClose, item, onManageStock }) => {
    const [history, setHistory] = useState([]);
    const [loading, setLoading] = useState(true);
    const callApi = useApi();
    const isMobile = useIsMobile(); // ✅ Check mobile state

    useEffect(() => {
        if (isOpen && item) {
            setLoading(true);
            callApi(`inventory/${item._id}/history`)
                .then(res => {
                    if (res.success) {
                        setHistory(res.data);
                    }
                    setLoading(false);
                });
        }
    }, [isOpen, item, callApi]);

    const titleContent = (
        <div className="mobile-modal-title">
            <FiActivity /> <span>History: {item?.name}</span>
        </div>
    );

    const footerContent = (
        <div className="mobile-modal-footer-btns">
            <button className="btn btn-secondary" onClick={onClose}>Close</button>
            {onManageStock && (
                <button className="btn btn-primary" onClick={() => { onClose(); onManageStock(item); }}>
                    <FiTruck className="btn-icon" /> Transfer
                </button>
            )}
        </div>
    );

    // ✅ Mobile Card Component
    const MobileHistoryCard = ({ log }) => (
        <div 
            className="mobile-generic-card" 
            style={{ 
                borderLeft: `4px solid ${log.changeAmount > 0 ? 'var(--green-500)' : 'var(--red-500)'}`,
                marginBottom: '0.75rem'
            }}
        >
            <div className="mobile-card-header" style={{ alignItems: 'flex-start' }}>
                <div className="mobile-card-text">
                    <span className="detail-label" style={{ fontSize: '0.75rem', color: 'var(--gray-500)' }}>
                        {new Date(log.createdAt).toLocaleString()}
                    </span>
                    <h4 className="mobile-card-title" style={{ marginTop: '0.25rem' }}>{log.reason}</h4>
                </div>
                <div style={{ textAlign: 'right' }}>
                    <span className={`qty-value ${log.changeAmount > 0 ? 'text-success' : 'text-danger'}`} style={{ fontSize: '1.1rem', fontWeight: 'bold' }}>
                        {log.changeAmount > 0 ? '+' : ''}{log.changeAmount}
                    </span>
                    <small className="qty-label" style={{ display: 'block', color: 'var(--gray-400)', fontSize: '0.7rem' }}>
                        Stock: {log.newQuantity}
                    </small>
                </div>
            </div>
            
            <div className="mobile-card-details" style={{ marginTop: '0.5rem', padding: '0.5rem', backgroundColor: 'var(--gray-50)' }}>
                <div className="mobile-detail-row">
                    <FiUser size={12} style={{ marginRight: '4px' }} /> 
                    <span>{log.performedBy?.name || 'System'}</span>
                </div>
                {log.affectedSerialNumbers?.length > 0 && (
                    <div className="sn-list-mobile">
                        {log.affectedSerialNumbers.map(sn => <span key={sn} className="detail-tag">SN: {sn}</span>)}
                    </div>
                )}
                {log.notes && (
                    <p style={{ fontSize: '0.8rem', color: 'var(--gray-500)', fontStyle: 'italic', marginTop: '4px' }}>
                        {log.notes}
                    </p>
                )}
            </div>
        </div>
    );

    return (
        <Modal
            isOpen={isOpen}
            onClose={onClose}
            title={titleContent}
            footer={footerContent}
            size="wide"
            className="inventory-history-modal"
        >
            {loading ? (
                <div className="loading-overlay"><div className="loading-spinner"></div></div>
            ) : (
                <>
                    {isMobile ? (
                        // ✅ Mobile View: Vertical Timeline Cards
                        <div className="mobile-inventory-list">
                            {history.length > 0 ? (
                                history.map((log) => <MobileHistoryCard key={log._id} log={log} />)
                            ) : (
                                <div className="no-tasks-message">No history records found.</div>
                            )}
                        </div>
                    ) : (
                        // ✅ Desktop View: Table
                        <div className="table-wrapper mobile-scroll-table">
                            <table>
                                <thead>
                                    <tr>
                                        <th>Date</th>
                                        <th className="hide-mobile">User</th>
                                        <th>Action</th>
                                        <th>Change</th>
                                        <th>Stock</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {history.map((log, index) => (
                                        <tr key={log._id || index}>
                                            <td className="date-cell">{new Date(log.createdAt).toLocaleDateString()}</td>
                                            <td className="hide-mobile">{log.performedBy?.name || 'System'}</td>
                                            <td>
                                                <span className="detail-tag">{log.reason}</span>
                                                {log.affectedSerialNumbers?.length > 0 && (
                                                    <div className="sn-list-mobile">
                                                        {log.affectedSerialNumbers.map(sn => <span key={sn}>SN: {sn}</span>)}
                                                    </div>
                                                )}
                                            </td>
                                            <td className={log.changeAmount > 0 ? 'text-success' : 'text-danger'}>
                                                {log.changeAmount > 0 ? '+' : ''}{log.changeAmount}
                                            </td>
                                            <td>{log.newQuantity}</td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                    )}
                </>
            )}
        </Modal>
    );
};

export default InventoryHistoryModal;